// Paste-tolerant parsing for the guided Microsoft Teams setup inputs.
// Customers routinely paste the whole `teams app create` credentials block
// (CLIENT_ID=… / CLIENT_SECRET=… / TENANT_ID=…) or an Azure-portal line into
// a single field, so each extractor pulls its labelled value first and falls
// back to the bare value.

const GUID_STRICT = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const GUID_ANYWHERE = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;

const labelledValue = (input: string, labels: ReadonlyArray<string>): string | null => {
  for (const label of labels) {
    // teams.cli prints `LABEL=value`; portal copy sometimes reads `Label: value`.
    const re = new RegExp(`${label}\\s*[=:]\\s*(\\S+)`, "i");
    const m = input.match(re);
    if (m && m[1]) return m[1];
  }
  return null;
};

// The bot's Application (client) ID. Accepts a bare GUID, or finds the
// CLIENT_ID/BOT ID line in a pasted block, or the first GUID anywhere.
export const extractAppId = (input: string): string | null => {
  const trimmed = input.trim();
  if (GUID_STRICT.test(trimmed)) return trimmed.toLowerCase();
  const labelled = labelledValue(trimmed, ["CLIENT_ID", "APP_ID", "Bot ID", "Application \\(client\\) ID"]);
  if (labelled !== null && GUID_STRICT.test(labelled)) return labelled.toLowerCase();
  const anywhere = trimmed.match(GUID_ANYWHERE);
  return anywhere ? anywhere[0].toLowerCase() : null;
};

// The customer's Microsoft directory (tenant) ID — also a GUID. Prefers the
// TENANT_ID label so a whole-block paste doesn't grab the CLIENT_ID by
// accident; a bare GUID or a single-GUID paste still works.
export const extractMsTenantId = (input: string): string | null => {
  const trimmed = input.trim();
  if (GUID_STRICT.test(trimmed)) return trimmed.toLowerCase();
  const labelled = labelledValue(trimmed, ["TENANT_ID", "Directory \\(tenant\\) ID"]);
  if (labelled !== null && GUID_STRICT.test(labelled)) return labelled.toLowerCase();
  const anywhere = trimmed.match(GUID_ANYWHERE);
  return anywhere ? anywhere[0].toLowerCase() : null;
};

// The client secret: free-form, so only a labelled extraction is safe on a
// block paste; otherwise take the trimmed input as-is. Rejects values that
// are clearly a pasted GUID (a secret is never a bare GUID — that's the
// wrong field) so a mis-paste reads as "not valid yet", not a silent 502.
export const extractClientSecret = (input: string): string | null => {
  const trimmed = input.trim();
  if (trimmed === "") return null;
  const labelled = labelledValue(trimmed, ["CLIENT_SECRET"]);
  if (labelled !== null) return labelled;
  if (GUID_STRICT.test(trimmed)) return null;
  // A whole-block paste without extracting anything labelled would push the
  // entire block as the secret; treat multi-line input as not-a-secret.
  if (trimmed.includes("\n")) return null;
  return trimmed;
};
